import React, { useRef, useState } from 'react';
import { CSSObject } from '@emotion/react';
import TextLink from '@cvent/carina/components/TextLink';
import OutlookIcon from '@images/icon_outlook.svg';
import GoogleIcon from '@images/icon_google.svg';
import YahooIcon from '@images/icon_yahoo.svg';
import { Col } from '@cvent/carina/components/Col';
import { Row } from '@cvent/carina/components/Row';
import { useTranslate } from 'nucleus-text';
import useTheme from '@hooks/useTheme';
import useBreakpoints from '@hooks/useBreakpoints';
import { useAnalytics } from '@hooks/useAnalytics';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { Keys } from '@tools/KeyConstants';
import { usePageActionsApi } from '@metrics/client/usePageActionsApi';
import Icon from '@components/shared/Icon';
import Popper from '@cvent/carina/components/Popper';
import { Placements } from '@cvent/carina/components/Popover';
import { Z_INDEX } from '@components/shared/zIndex';
import Image from '../shared/Image';
import { downloadCalendar, getGoogleCalendar, getYahooCalendar, getOutlookCalendar } from './calendarUtils';

interface Props {
  start: string;
  end: string;
  name: string;
  sessionId: string;
  isFeatured?: boolean;
  sessionDescription: string;
  eventName: string;
  locationName?: string;
  showPlusIcon?: boolean;
  hideCalendarIcon?: boolean;
}

const useStyles = (hideCalendarIcon?: boolean): Record<string, CSSObject> => {
  const { isMobile } = useBreakpoints();
  const { backgroundColor, size, depth } = useTheme();
  const boxShadowProperties = depth.boxShadow[4];
  const boxShadow = `${boxShadowProperties.xOffset} ${boxShadowProperties.yOffset} ${boxShadowProperties.blurRadius} ${boxShadowProperties.spreadRadius} ${boxShadowProperties.color}`;

  return {
    calendarOptionsRow: {
      padding: '8px 16px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: backgroundColor.hover
      }
    },
    addToCalendar: {
      marginTop: !isMobile ? 0 : 14,
      alignSelf: !isMobile ? 'center' : '',
      ...(hideCalendarIcon && {
        marginTop: 0
      })
    },
    addToCalendarText: {
      ...(hideCalendarIcon && {
        textDecoration: 'underline',
        fontSize: isMobile ? 16 : 20
      })
    },
    calendarOptions: {
      padding: '8px 0px',
      position: 'relative',
      backgroundColor: backgroundColor.base,
      borderRadius: size.border.radius.fiveX,
      boxShadow,
      zIndex: Z_INDEX.ABOVE_ENGAGEMENT_PANEL
    },
    optionImage: { width: '13px', height: '13px' },
    yahooImage: { width: '15px', height: '15px' }
  };
};

function CalendarOption({
  icon,
  name,
  onClick
}: {
  icon: JSX.Element;
  name: string;
  onClick: () => void;
}): JSX.Element {
  const { calendarOptionsRow } = useStyles();
  return (
    <div
      aria-label={name}
      role="link"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={event => {
        if (event.key === Keys.ENTER) {
          onClick();
        }
      }}
      css={calendarOptionsRow}
    >
      <Row>
        <Col
          padding={0}
          width={15}
          flex={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Col>
        <Col
          width="content"
          flex={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {name}
        </Col>
      </Row>
    </div>
  );
}

function AddToCalendar({
  start,
  end,
  name,
  sessionId,
  isFeatured = false,
  sessionDescription,
  eventName,
  locationName = '',
  showPlusIcon = false,
  hideCalendarIcon = false
}: Props): JSX.Element {
  const { translate } = useTranslate();
  const analytics = useAnalytics();
  const { addToCalendar, calendarOptions, addToCalendarText, optionImage, yahooImage } = useStyles(hideCalendarIcon);
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const popoverToggle = useRef(null);
  const { eventAppAddToCalendar } = usePageActionsApi();
  const [popperZIndex, setPopperZIndex] = useState<'auto' | number>(Z_INDEX.ABOVE_ENGAGEMENT_PANEL);

  let CalendarIcon = <Icon name="external-link" size="xs" aria-hidden />;
  if (showPlusIcon) {
    CalendarIcon = <Icon name="plus-circle" size="xs" aria-hidden />;
  }
  if (hideCalendarIcon) {
    CalendarIcon = null;
  }

  const sendCalendarFact = calendarType => {
    if (analytics) {
      eventAppAddToCalendar({
        sessionId,
        isFeatured,
        sessionName: name,
        calendarType
      });
    }
  };

  const onDownloadCalendar = () => {
    sendCalendarFact('Download');
    downloadCalendar({
      start,
      end,
      name,
      description: sessionDescription,
      eventName,
      location: locationName
    });
    setIsPopperOpen(false);
  };

  return (
    <div>
      <TextLink
        onClick={() => setIsPopperOpen(current => !current)}
        ref={popoverToggle}
        aria-haspopup="true"
        aria-expanded={isPopperOpen}
        element="button"
        icon={CalendarIcon}
        size="l"
        css={addToCalendar}
        {...injectTestId('session-detail-add-to-calendar')}
      >
        {/* Notifies the user that the link will open in a new tab for accessibility purposes. */}
        <span
          css={{
            position: 'absolute',
            width: 1,
            clip: 'rect(0 0 0 0)'
          }}
        >
          ({translate('SessionDetails-AddToCalendar-OpensInNewTab')})
        </span>
        <span css={addToCalendarText}>{translate('Universal-AddToPersonalCalendar')}</span>
      </TextLink>
      <Popper
        placement={Placements.bottomStart}
        flip
        isOpen={isPopperOpen}
        triggerRef={popoverToggle}
        onDismiss={() => setIsPopperOpen(false)}
        zIndex={popperZIndex}
        popperjsModifiers={[
          {
            name: 'updateZIndex',
            enabled: true,
            phase: 'main',
            fn: ({ state }) =>
              //  Move the popper under the engagement panel when the add to calendar link is not visible
              setPopperZIndex(state.placement === 'top-start' ? 'auto' : Z_INDEX.ABOVE_ENGAGEMENT_PANEL)
          }
        ]}
      >
        <div
          css={calendarOptions}
          onBlur={event => {
            // Close popover when it is tabbed out of
            if (event.relatedTarget && event.relatedTarget.parentNode !== event.currentTarget) {
              if (popoverToggle.current) {
                setIsPopperOpen(false);
              }
            }
          }}
        >
          <CalendarOption
            name={translate('SessionDetails-AddToCalendar-GoogleCalendar')}
            icon={<Image src={GoogleIcon} style={optionImage} />}
            onClick={() => {
              sendCalendarFact('Google');
              const url = getGoogleCalendar({
                name,
                start,
                end,
                eventName
              });
              window.open(url, '_blank');
              setIsPopperOpen(false);
            }}
          />
          <CalendarOption
            icon={<Image src={OutlookIcon} style={optionImage} />}
            name={translate('SessionDetails-AddToCalendar-OutlookCalendar')}
            onClick={() => {
              sendCalendarFact('Outlook');
              const url = getOutlookCalendar({
                name,
                start,
                end,
                eventName
              });
              window.open(url, '_blank');
              setIsPopperOpen(false);
            }}
          />
          <CalendarOption
            icon={<Image src={YahooIcon} style={yahooImage} />}
            name={translate('SessionDetails-AddToCalendar-YahooCalendar')}
            onClick={() => {
              sendCalendarFact('Yahoo');
              const url = getYahooCalendar({
                name,
                start,
                end,
                eventName
              });
              window.open(url, '_blank');
              setIsPopperOpen(false);
            }}
          />
          <CalendarOption
            icon={<Icon name="download" size="xs" aria-hidden />}
            name={translate('SessionDetails-AddToCalendar-CalendarDownload')}
            onClick={onDownloadCalendar}
          />
        </div>
      </Popper>
    </div>
  );
}

export default AddToCalendar;
